'use client'
import Link from 'next/link'
import { useContextElement } from '@/context/Context'
import React, { useEffect, useState } from 'react'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import styles from './CartDrawer.module.css'

export default function CartDrawer() {
  const {
    cartProducts,
    totalPrice,
    addProductToCart,
    removeProductFromCart,
    checkCartInventory,
    checkCartPrice,
  } = useContextElement()

  // 驗證金額與庫存
  const [cartCheckResult, setCartCheckResult] = useState({
    removedProducts: [],
    priceChangedProducts: [],
  })

  const [showCartCheckDialog, setShowCartCheckDialog] = useState(false)

  const pathname = usePathname()

  const closeCart = () => {
    document
      .getElementById('cartDrawerOverlay')
      ?.classList.remove('page-overlay_visible')
    document.getElementById('cartDrawer')?.classList.remove('aside_visible')
  }

  const handleCheckout = async (e) => {
    e.preventDefault()

    let removedProducts = []
    let priceChangedProducts = []

    // 1. 檢查庫存
    const inventoryResult = await checkCartInventory()

    if (!inventoryResult?.success) {
      alert('購物車庫存檢查失敗，請稍後再試')
      return
    }

    if (inventoryResult.hasRemovedProduct) {
      removedProducts = inventoryResult.removedProducts || []
    }

    // 2. 檢查價格
    const priceResult = await checkCartPrice()

    if (!priceResult?.success) {
      alert('購物車價格檢查失敗，請稍後再試')
      return
    }

    if (priceResult.hasPriceChanged) {
      priceChangedProducts = priceResult.changedProducts || []
    }

    // 3. 有任何異動 -> 先開 Modal
    if (removedProducts.length > 0 || priceChangedProducts.length > 0) {
      setCartCheckResult({
        removedProducts,
        priceChangedProducts,
      })

      setShowCartCheckDialog(true)
      return
    }

    // 4. 完全沒異動 -> 直接進購物車頁
    pushBeginCheckout()

    window.location.href = '/shopping-cart'
  }

  const setQuantity = (ProductID, quantity) => {
    if (quantity >= 1) {
      const current = cartProducts.find((p) => p.ProductID === ProductID)
      if (quantity > current.LimitedQty) {
        alert(`\n超過此商品購買上限: ${current.LimitedQty}`)
        return
      }
      if (quantity > current.InventoryQty) {
        alert(`\n超過此商品庫存，故無法加入購物車`)
        return
      }
      if (current && current.Qty !== quantity) {
        const updated = { ...current, Qty: quantity }
        // 呼叫 context 中的邏輯來更新
        addProductToCart({ ProductID: ProductID, updated: updated })
      }
    } else {
      alert(`\n商品購買數量不得為 0`)
      return
    }
  }

  useEffect(() => {
    const drawer = document.getElementById('cartDrawer')

    if (!drawer) return

    const lockBody = () => {
      document.body.style.overflow = 'hidden'
      document.body.style.height = '100vh'
    }

    const unlockBody = () => {
      document.body.style.overflow = ''
      document.body.style.height = ''
    }

    // 初始判斷一次（避免一開始就開著）
    if (drawer.classList.contains('aside_visible')) {
      lockBody()
    }

    // 監聽 class 變化
    const observer = new MutationObserver(() => {
      if (drawer.classList.contains('aside_visible')) {
        lockBody()
      } else {
        unlockBody()
      }
    })

    observer.observe(drawer, {
      attributes: true,
      attributeFilter: ['class'],
    })

    return () => {
      observer.disconnect()
      unlockBody()
    }
  }, [])

  useEffect(() => {
    closeCart()
  }, [pathname])

  // 💡 修正總數計算：確保 Qty 為 0 的配件不會被 (p.Qty || 1) 強制算成 1 筆
  const totalCartItemsCount = cartProducts.reduce(
    (sum, p) => sum + (p.Qty ?? 0),
    0
  )

  const pushBeginCheckout = () => {
    if (typeof window === 'undefined') return
    if (!cartProducts?.length) return

    const validItems = cartProducts.filter((item) => Number(item.Qty || 0) > 0)

    if (!validItems.length) return

    window.dataLayer = window.dataLayer || []

    window.dataLayer.push({ ecommerce: null })

    window.dataLayer.push({
      event: 'begin_checkout',
      ecommerce: {
        currency: 'TWD',
        value: validItems.reduce(
          (sum, item) => sum + Number(item.Price || 0) * Number(item.Qty || 0),
          0
        ),
        items: validItems.map((item) => ({
          item_id: String(item.ProductID ?? ''),
          item_name: item.ProductName || '',
          item_variant: item.SkuID ? String(item.SkuID) : '',
          price: Number(item.Price || 0),
          quantity: Number(item.Qty || 0),
          is_preorder: Boolean(item.IsPreorder),
        })),
      },
    })
  }

  return (
    <>
      <div className={`aside aside_right ${styles.drawer}`} id="cartDrawer">
        <div
          className={`aside-header d-flex align-items-center ${styles.header}`}
        >
          <h3 className="text-uppercase fs-6 mb-0">
            購物車清單 (
            <span className="cart-amount js-cart-items-count">
              {totalCartItemsCount}
            </span>
            )
          </h3>
          <button
            onClick={closeCart}
            className="btn-close-lg js-close-aside btn-close-aside ms-auto"
          ></button>
        </div>

        {cartProducts.length ? (
          <div
            className={`aside-content cart-drawer-items-list ${styles.content}`}
          >
            {cartProducts.map((item, i) => {
              // 💡 判斷是否為 0 元配件
              const isAccessory = item.Qty === 0

              return (
                <React.Fragment key={i}>
                  <div className="cart-drawer-item d-flex position-relative">
                    <div className="position-relative">
                      <div className={styles.imgWrap}>
                        <Image
                          loading="lazy"
                          fill
                          className={styles.productImg}
                          src={
                            item.PictureData?.[0]?.Name ||
                            '/assets/images/noimage.jpg'
                          }
                          alt="image"
                          sizes="80px"
                        />
                      </div>
                    </div>
                    <div className="cart-drawer-item__info flex-grow-1">
                      <h6
                        className="cart-drawer-item__title fw-normal"
                        style={{ paddingRight: '1.25rem' }}
                      >
                        {item.ProductName}
                        {`${item?.IsPreorder ? ' (預購)' : ''}`}
                      </h6>
                      <p className="cart-drawer-item__option text-secondary">
                        {item.ParentID || ''}
                      </p>
                      <p className="cart-drawer-item__option text-secondary">
                        {item.SkuName || ''}
                      </p>
                      <div className="d-flex align-items-center justify-content-between mt-1">
                        {isAccessory ? (
                          /* 💡 配件：隱藏加減控制項，僅用純文字展示數量 */
                          <div className="text-secondary fs-14">
                            數量: 0{' '}
                            {/* 因資料庫存 0 代表附屬，畫面上通常顯示 1 件隨附 */}
                          </div>
                        ) : (
                          /* 💡 一般商品：保留完整加減控制項 */
                          <div className="qty-control position-relative">
                            <input
                              type="number"
                              name="quantity"
                              value={item.Qty}
                              min="1"
                              onChange={(e) =>
                                setQuantity(item.ProductID, +e.target.value)
                              }
                              className="qty-control__number border-0 text-center"
                            />
                            <div
                              onClick={() =>
                                setQuantity(item.ProductID, item.Qty - 1)
                              }
                              className="qty-control__reduce text-start"
                            >
                              -
                            </div>
                            <div
                              onClick={() =>
                                setQuantity(item.ProductID, item.Qty + 1)
                              }
                              className="qty-control__increase text-end"
                            >
                              +
                            </div>
                          </div>
                        )}
                        <span className="cart-drawer-item__price money price">
                          ${item.Price * item.Qty}
                        </span>
                      </div>
                    </div>

                    {/* 💡 配件商品不能被移除：只有當非配件時，才渲染刪除按鈕 */}
                    {!isAccessory && (
                      <button
                        onClick={() =>
                          removeProductFromCart(item.ProductID, item.SkuID)
                        }
                        className="btn-close-xs position-absolute top-0 end-0 js-cart-item-remove"
                      ></button>
                    )}
                  </div>
                  <hr className="cart-drawer-divider" />
                </React.Fragment>
              )
            })}
          </div>
        ) : (
          <div className="fs-18 mt-5 px-5">購物車目前是空的</div>
        )}

        <div
          className={`cart-drawer-actions position-absolute start-0 bottom-0 w-100 ${styles.actions}`}
        >
          <hr className="cart-drawer-divider" />
          <div className="d-flex justify-content-between">
            <h6 className="fs-base fw-medium">商品總計:</h6>
            <span className="cart-subtotal fw-medium">${totalPrice}</span>
          </div>

          {cartProducts.length ? (
            <Link
              href="/shopping-cart"
              className="btn btn-primary mt-3 d-block"
              onClick={handleCheckout}
            >
              前往結帳
            </Link>
          ) : (
            <Link href="/product-list" className="btn btn-light mt-3 d-block">
              前往購物
            </Link>
          )}
        </div>
      </div>

      <div
        id="cartDrawerOverlay"
        onClick={closeCart}
        className="page-overlay"
      ></div>

      {showCartCheckDialog && (
        <div className={styles.inventoryModalOverlay}>
          <div className={styles.inventoryModal}>
            <h5 className="mb-3">購物車內容已更新</h5>

            <p className="mb-3">
              部分商品的庫存或價格已發生變動，請重新確認購物車內容。
            </p>

            <div className={styles.removedProductList}>
              {/* ========================= */}
              {/* 庫存異動 */}
              {/* ========================= */}
              {cartCheckResult.removedProducts.length > 0 && (
                <>
                  <div className="fw-bold mb-2">已移除商品</div>

                  {cartCheckResult.removedProducts.map((item, index) => (
                    <div
                      key={`removed-${item.ProductID}-${item.GoodListID}-${index}`}
                      className={styles.removedProductItem}
                    >
                      <div className="fw-medium">
                        {item.ProductName || `商品 ${item.ProductID}`}
                      </div>

                      {item.ParentID && (
                        <div className="text-secondary fs-14">
                          {item.ParentID}
                        </div>
                      )}

                      {item.CombinationOptionName && (
                        <div className="text-secondary fs-14">
                          {item.CombinationOptionName}
                        </div>
                      )}

                      <div className="fs-14 mt-1" style={{ color: '#dc2626' }}>
                        因庫存不足已自動移除
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* ========================= */}
              {/* 價格異動 */}
              {/* ========================= */}
              {cartCheckResult.priceChangedProducts.length > 0 && (
                <>
                  <div
                    className="fw-bold mb-2"
                    style={{
                      marginTop:
                        cartCheckResult.removedProducts.length > 0
                          ? '16px'
                          : '0',
                    }}
                  >
                    價格異動商品
                  </div>

                  {cartCheckResult.priceChangedProducts.map((item, index) => (
                    <div
                      key={`price-${item.ProductID}-${item.GoodListID}-${index}`}
                      className={styles.removedProductItem}
                    >
                      <div className="fw-medium">
                        {item.ProductName || `商品 ${item.ProductID}`}
                      </div>

                      {item.ParentID && (
                        <div className="text-secondary fs-14">
                          {item.ParentID}
                        </div>
                      )}

                      {item.CombinationOptionName && (
                        <div className="text-secondary fs-14">
                          {item.CombinationOptionName}
                        </div>
                      )}

                      <div className="mt-2 fs-14">
                        <span className="text-secondary">原價格：</span>

                        <span
                          style={{
                            textDecoration: 'line-through',
                            color: '#999',
                          }}
                        >
                          ${Number(item.OldPrice || 0).toLocaleString()}
                        </span>
                      </div>

                      <div className="fs-14">
                        <span className="text-secondary">最新價格：</span>

                        <span
                          className="fw-medium"
                          style={{ color: '#dc2626' }}
                        >
                          ${Number(item.NewPrice || 0).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>

            <button
              type="button"
              className="btn btn-primary w-100 mt-3"
              onClick={() => {
                const hasRemovedProduct =
                  cartCheckResult.removedProducts.length > 0

                setShowCartCheckDialog(false)

                setCartCheckResult({
                  removedProducts: [],
                  priceChangedProducts: [],
                })

                // 有商品因庫存不足被移除，不跳轉
                if (hasRemovedProduct) {
                  return
                }

                // 只有價格異動，才繼續前往購物車頁
                pushBeginCheckout()
                window.location.href = '/shopping-cart'
              }}
            >
              我知道了
            </button>
          </div>
        </div>
      )}
    </>
  )
}
